import React, { useEffect, useState } from "react";
import { Modal, Flex, Tooltip, Button } from "antd";
import CustomTable from "../../Component/Table";
import { getCampaignRecipients } from "../../Services/campaign";
import { useQuery } from "@tanstack/react-query";
import RecipientEmailDataModal from "./RecipientEmailDataModal";

const CampaignRecipientsModal = ({ isModalVisible, handleCancel, campaignData }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [campaignId, setCampaignId] = useState(null);
  const [isEmailDataModalVisible, setIsEmailDataModalVisible] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null);

  const skip = (currentPage - 1) * pageSize;

  const { data, isLoading } = useQuery({
    queryKey: ["campaign_recipients", currentPage, pageSize, campaignId],
    queryFn: () => getCampaignRecipients({ campaign_id: campaignId, skip, limit: pageSize }),
    enabled: !!campaignId && isModalVisible,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (isModalVisible) {
      setCampaignId(campaignData?.id);
      setCurrentPage(1);
    }
  }, [isModalVisible, campaignData]);

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const columns = [
    { title: "User Id", dataIndex: "user_id", key: "user_id" },
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Email", dataIndex: "email", key: "email" },
    {
      title: "Itinerary",
      dataIndex: "itinerary_headline",
      key: "itinerary_headline",
      render: (text) =>
        text && text.length > 30 ? (
          <Tooltip title={text}>{text.slice(0, 30) + "..."}</Tooltip>
        ) : (
          text || "-"
        ),
    },
    {
      title: "Sent",
      dataIndex: "is_sent",
      key: "is_sent",
      render: (val) => (val ? "Yes" : "No"),
    },
    {
      title: "Downloaded",
      dataIndex: "is_downloaded",
      key: "is_downloaded",
      render: (val) => (val ? "Yes" : "No"),
    },
    { title: "Download Count", dataIndex: "download_count", key: "download_count" },
    { title: "Useful Count", dataIndex: "useful_count", key: "useful_count" },
    { title: "Not Useful Count", dataIndex: "not_useful_count", key: "not_useful_count" },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      render: (_, record) => (
        <Button
          size="small"
          onClick={() => {
            setSelectedRecipient(record);
            setIsEmailDataModalVisible(true);
          }}
        >
          View Content
        </Button>
      ),
    },
  ];

  return (
    <Modal
      title=""
      open={isModalVisible}
      footer={null}
      width={900}
      onCancel={handleCancel}
      maskClosable={false}
    >
      <Flex justify="space-between" align="center">
        <h3>#{campaignId} {campaignData?.title} - Recipients</h3>
      </Flex>
      <br />
      <CustomTable
        columns={columns}
        data={data?.recipients || []}
        loading={isLoading}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: data?.total_count || 0,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
          position: ["bottomRight"],
        }}
        onTableChange={handleTableChange}
        maxHeight="calc(100vh - 300px)"
      />
      <RecipientEmailDataModal
        isModalVisible={isEmailDataModalVisible}
        handleCancel={() => setIsEmailDataModalVisible(false)}
        emailData={selectedRecipient?.email_data}
        recipientLabel={selectedRecipient ? `${selectedRecipient.name} (${selectedRecipient.email})` : ""}
      />
    </Modal>
  );
};

export default CampaignRecipientsModal;
